import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Skeleton, Theme, useMediaQuery } from '@mui/material';
import { isEmpty } from 'lodash-es';

import { RecommendationChipData, areasDescriptionMap } from '@common-types';
import { CoreBox, CoreTypography } from '@youscience/core';

import { CollegeLocation } from '@interfaces/location';

import useFavoriteMajorsStore from '@store/FavoriteMajorsStore';
import useFavoriteCareersStore from '@store/FavoriteCareersStore';
import useHomePageStore from '@store/HomePageStore';
import useMajorDetailsStore from '@store/MajorDetailsStore';
import useUserPreferencesStore from '@store/UserPreferencesStore';

import { MajorSurveyModalProps, withMajorSurveyModal } from '@HOCs/withMajorSurveyModal';

import useAppInitStore from '@store/AppInitStore';

import { getMajorIconSrc, getMajorImageSrc, renderPersonalRecommendationChips } from '@utils/major';
import { getBreadcrumbs } from '@utils/breadcrumbs';

import { BREADCRUMBS_PAGE_TEXT } from '@constants/breadcrumbs';

import { Breadcrumbs } from '@components/Breadcrumbs';
import { Container } from '@components/Layout/components/Container';
import { CardSlider, CardType } from '@components/CardSlider';

import HeartIcon from '@images/heart.svg?react';
import FilledHeartIcon from '@images/heart-filled.svg?react';
import NotesIcon from '@images/notes.svg?react';

import { RelatedCertifications } from './components/RelatedCertifications';
import { WhereYouCanStudyIt } from './components/WhereYouCanStudyIt';

import {
  sxStyles,
  StyledHeadSectionInner,
  StyledHeadSectionInfo,
  StyledPersonalRecommendation,
  StyledHeadSectionActions,
  StyledFavoritesButton,
  StyledNotes,
  StyledMajorPhotoWrap,
  StyledAboutSectionInner,
} from './MajorDetails.styles';
import { StyledAreaIcon } from '@components/MajorCard/MajorCard.styles';
import { useStateSpotlight } from '@hooks/useStateSpotlight';

type MajorDetailsProps = MajorSurveyModalProps;
interface RecommendedChipDataExtended extends RecommendationChipData {
  icon?: ReactNode;
}

const MajorDetails = (props: MajorDetailsProps) => {
  const { onOpenSurvey } = props;

  const { cipCode = '' } = useParams();
  const location: CollegeLocation = useLocation();

  const [isShowLessDescription, setIsShowLessDescription] = useState(false);
  const isDesktopScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));

  const { cipCodes: favoriteMajorsCipCodes } = useUserPreferencesStore((state) => state.majorFavorites);
  const favoriteCareersStore = useFavoriteCareersStore((state) => state.favoriteCareers);
  const landingPageSchoolData = useHomePageStore((state) => state.landingPageSchoolData);
  const isLandingPage = !!landingPageSchoolData?.id;
  const selectedSchoolName = useHomePageStore((state) => state.landingPageSchoolData?.cardInfo?.name);
  const isMajorDetailDataLoading = useMajorDetailsStore((state) => state.isMajorDetailDataLoading);
  const adminHomePageData = useHomePageStore((state) => state.adminHomePageData);

  const { isLoading: isInitialDataLoading } = useAppInitStore((store) => store);
  const { spotlight, isLoaded: isSpotlightLoaded } = useStateSpotlight();
  const isUsheFirst = spotlight?.stateCode === 'UT' && !isLandingPage;

  const showSkeletonLoading = isInitialDataLoading || isMajorDetailDataLoading || !isSpotlightLoaded;

  const {
    schools,
    relatedCareers,
    isFavoriteSoc,
    isBestFitSoc,
    hasCertification,
    userCertifications,
    cipTitle,
    employers,
  } = useMajorDetailsStore((state) => state.majorDetailData);

  const getFavoriteCareersStore = useFavoriteCareersStore((state) => state.getFavoriteCareers);
  const toggleFavoriteMajors = useFavoriteMajorsStore((state) => state.toggleFavoriteMajors);
  const getMajorDetailData = useMajorDetailsStore((state) => state.getMajorDetailData);

  const areaCipCode = Math.floor(+cipCode);
  const areaIconSrc = getMajorIconSrc(areaCipCode);
  const areaImageSrc = getMajorImageSrc(areaCipCode);
  const description = areasDescriptionMap.get(areaCipCode)?.cipDescription ?? '';
  const recommendationValues = { isFavoriteSoc, isBestFitSoc, hasCertification };

  const homePageName = adminHomePageData?.ipedsId ? BREADCRUMBS_PAGE_TEXT.ADMIN_HOME_PAGE : selectedSchoolName;

  const pageBreadcrumbs = getBreadcrumbs(location.state?.from, BREADCRUMBS_PAGE_TEXT.MAJOR_DETAILS, homePageName);

  const isFavoriteMajor = useMemo(() => {
    return !!favoriteMajorsCipCodes?.find((majorCipCode) => majorCipCode === cipCode);
  }, [favoriteMajorsCipCodes, cipCode]);

  const handleOpenSurvey = () => {
    onOpenSurvey?.(cipCode);
  };

  const handleAddToFavorites = () => {
    if (!isFavoriteMajor) {
      handleOpenSurvey();
    }

    void toggleFavoriteMajors(cipCode);
  };

  useEffect(() => {
    if (!isSpotlightLoaded) return;

    const fetchData = async () => {
      if (isEmpty(favoriteCareersStore)) {
        await getFavoriteCareersStore();
      }

      await getMajorDetailData(cipCode, isUsheFirst);
    };

    void fetchData();
  }, [cipCode, isSpotlightLoaded, isUsheFirst]);

  const handleShowMoreDescription = () => {
    setIsShowLessDescription((prevState) => !prevState);
  };

  const getSchoolDescription = () => {
    if (description?.length <= 400) return description;

    return (
      <>
        {isShowLessDescription ? description : `${description.slice(0, 400)}...`} &nbsp;
        <CoreTypography sx={sxStyles.showMoreButton} onClick={handleShowMoreDescription} component="span">
          {isShowLessDescription ? 'show less' : 'show more'}
        </CoreTypography>
      </>
    );
  };

  const renderPersonalRecommendationItem = (chipData: RecommendedChipDataExtended, isRenderText: boolean) => {
    const { icon, label, color } = chipData;

    return (
      <StyledPersonalRecommendation key={label} color={color}>
        {icon}

        {(isRenderText || isDesktopScreen) && (
          <CoreTypography component="div" variant="caption">
            {label}
          </CoreTypography>
        )}
      </StyledPersonalRecommendation>
    );
  };

  return (
    <>
      <CoreBox sx={sxStyles.headSectionBg}>
        <StyledHeadSectionInner>
          <StyledHeadSectionInfo>
            <Breadcrumbs links={pageBreadcrumbs.links} currentPage={pageBreadcrumbs.currentPage} />

            <CoreBox sx={sxStyles.headSectionText}>
              {!isMajorDetailDataLoading && (
                <CoreBox>
                  {renderPersonalRecommendationChips(recommendationValues, renderPersonalRecommendationItem)}
                </CoreBox>
              )}

              {showSkeletonLoading ? (
                <>
                  <CoreBox sx={sxStyles.areaTitle}>
                    <Skeleton variant="text" width={650} height={52} />
                  </CoreBox>

                  <Skeleton variant="text" width={650} height={104} />
                </>
              ) : (
                <>
                  <CoreBox sx={sxStyles.areaTitle}>
                    <CoreBox>
                      <StyledAreaIcon imageSrc={areaIconSrc} />
                    </CoreBox>

                    <CoreTypography variant="overline" component="h6">
                      {areasDescriptionMap.get(areaCipCode)?.cipTitle}
                    </CoreTypography>
                  </CoreBox>

                  <CoreTypography data-testid="majorTitle" sx={sxStyles.majorTitle} component="div" variant="h1">
                    {cipTitle}
                  </CoreTypography>
                </>
              )}
            </CoreBox>

            <StyledHeadSectionActions>
              {showSkeletonLoading ? (
                <Skeleton variant="rectangular" width={236} height={48} />
              ) : (
                <>
                  <StyledFavoritesButton
                    data-testid={`${isFavoriteMajor ? 'removeFrom' : 'addTo'}FavoritesButton`}
                    onClick={handleAddToFavorites}
                  >
                    {isFavoriteMajor ? (
                      <>
                        <FilledHeartIcon />
                        Remove from favorites
                      </>
                    ) : (
                      <>
                        <HeartIcon />
                        Add to favorites
                      </>
                    )}
                  </StyledFavoritesButton>

                  {isFavoriteMajor && (
                    <StyledNotes onClick={handleOpenSurvey}>
                      <NotesIcon />
                      Review your notes
                    </StyledNotes>
                  )}

                  {/* TODO will deploy in next release */}
                  {/* <StyledWebsiteLink target="_blank" href="/"> */}
                  {/*   <UrlIcon /> */}
                  {/*   CIP Code Detail Website */}
                  {/* </StyledWebsiteLink> */}
                </>
              )}
            </StyledHeadSectionActions>
          </StyledHeadSectionInfo>

          <StyledMajorPhotoWrap>
            {showSkeletonLoading ? (
              <Skeleton variant="rectangular" width={528} height={352} />
            ) : (
              <img src={areaImageSrc} alt="Area" />
            )}
          </StyledMajorPhotoWrap>
        </StyledHeadSectionInner>
      </CoreBox>

      <CoreBox>
        <Container>
          <CoreBox sx={sxStyles.aboutWrap}>
            <StyledAboutSectionInner>
              <CoreTypography sx={sxStyles.aboutTitle} component="div" variant="h3">
                About
              </CoreTypography>

              {showSkeletonLoading ? (
                <Skeleton variant="rectangular" width={640} height={72} />
              ) : (
                <CoreTypography sx={sxStyles.aboutDescription} component="p">
                  {getSchoolDescription()}
                </CoreTypography>
              )}
            </StyledAboutSectionInner>
          </CoreBox>
        </Container>

        {spotlight && isUsheFirst && !showSkeletonLoading && (
          <WhereYouCanStudyIt cipCode={cipCode} schools={schools ?? []} spotlight={spotlight} />
        )}

        {(showSkeletonLoading || relatedCareers?.length > 0) && (
          <CardSlider
            cardType={CardType.CAREER}
            title="Related careers"
            cardList={relatedCareers}
            isDataLoading={showSkeletonLoading}
          />
        )}

        {!isUsheFirst && !isLandingPage && (showSkeletonLoading || schools?.length > 0) && (
          <CoreBox sx={sxStyles.nearbySchoolsBg}>
            <CardSlider
              cardType={CardType.SCHOOL}
              title="Nearby schools that offer this program"
              cardList={schools}
              isDataLoading={showSkeletonLoading}
            />
          </CoreBox>
        )}

        {(showSkeletonLoading || employers?.length > 0) && (
          <CardSlider
            cardType={CardType.EMPLOYER}
            title="The following employer sponsors are looking for graduates of this program"
            cardList={employers}
            isDataLoading={showSkeletonLoading}
          />
        )}

        <RelatedCertifications relatedCertifications={userCertifications} isDataLoading={showSkeletonLoading} />
      </CoreBox>
    </>
  );
};

export const MajorDetailsWithModal = withMajorSurveyModal(MajorDetails);
